import {
  JSONRPCClient,
  JSONRPCServer,
  JSONRPCServerAndClient,
} from 'json-rpc-2.0';
import { v4 as uuidv4 } from 'uuid';
import * as Sentry from '@sentry/browser';

type LoginResponse = {
  accountDisabled: boolean;
  accountLocked: boolean;
  alreadyLoggedIn: boolean;
  changePassword: boolean;
  email: string;
  groups: string;
  logo: string;
  reason: string;
  sessionUuid: string;
  success: boolean;
  userName: string;
  userUuid: string;
};

export interface Session {
  request: (method: string, params: any) => PromiseLike<any>;
  serverAndClient: JSONRPCServerAndClient | undefined;
  webSocket: WebSocket | undefined;
  socketId: string;
  type: 'olSession' | 'dbSession' | 'replaySession';
}

type SessionType = 'olSession' | 'dbSession' | 'replaySession';

function dispatchCustomEvent(eventName: string, detail: any = null): void {
  const event = new CustomEvent(eventName, { detail: detail });
  window.dispatchEvent(event);
}

function dispatchWebSocketCloseEvent(websocket: WebSocket) {
  if (websocket.url.includes('olserver')) {
    dispatchCustomEvent('connectionClosed', 'olSession');
  } else if (websocket.url.includes('dbserver')) {
    dispatchCustomEvent('connectionClosed', 'dbSession');
  } else if (websocket.url.includes('replay')) {
    dispatchCustomEvent('connectionClosed', 'replaySession');
  } else {
    dispatchCustomEvent('connectionClosed');
  }
}

function initializeWebSocketEventHandlers(
  serverwebSocket: WebSocket,
  serverAndClient: { receiveAndSend: (messageData: any) => void }
): void {
  serverwebSocket.onmessage = (event: MessageEvent) => {
    try {
      const messageData = JSON.parse(event.data.toString());

      // Check for session errors BEFORE passing through for immediate detection
      if (messageData.error) {
        const errorMessage = messageData.error.message || '';
        if (
          errorMessage.includes('Invalid session ID') ||
          errorMessage.includes('SessionNotFound')
        ) {
          console.warn('Session error detected:', errorMessage);

          // Capture to Sentry
          Sentry.captureException(new Error(errorMessage), {
            tags: {
              error_type: errorMessage.includes('Invalid session ID')
                ? 'invalid_session_id'
                : 'session_not_found',
            },
            extra: {
              error: messageData.error,
              url: serverwebSocket.url,
            },
          });

          // Dispatch the session error event immediately
          dispatchCustomEvent('sessionError');

          // Still pass through to handle the response properly
          serverAndClient.receiveAndSend(messageData);
          return;
        }

        // Capture any other errors that aren't session-related
        Sentry.captureException(
          new Error(`WebSocket error: ${messageData.error.message}`),
          {
            tags: { error_type: 'websocket_message_error' },
            extra: {
              error: messageData.error,
              url: serverwebSocket.url,
            },
          }
        );
      }

      // Handle special server-initiated messages
      if (messageData.method === 'update') {
        dispatchCustomEvent('eventNotification', messageData.params);
        return;
      }

      if (messageData.method === 'overlay') {
        dispatchCustomEvent('replayOverlay', messageData.params);
        return;
      }

      // Pass everything else through
      serverAndClient.receiveAndSend(messageData);
    } catch (e) {
      console.error('Failed to parse message data:', e);

      Sentry.captureException(e, {
        tags: {
          error_type: 'websocket_parse_error',
        },
        extra: {
          url: serverwebSocket.url,
          rawData: event.data?.toString?.().substring(0, 500),
        },
      });
    }
  };

  serverwebSocket.onclose = (closeEvent: CloseEvent) => {
    console.error(
      `WebSocket Closed with url ${serverwebSocket.url}:`,
      closeEvent
    );

    // Only capture unexpected closes as errors
    if (!closeEvent.wasClean || closeEvent.code !== 1000) {
      Sentry.captureException(
        new Error(
          `Unexpected WebSocket close: ${closeEvent.reason || 'No reason'}`
        ),
        {
          tags: {
            error_type: 'unexpected_close',
            close_code: closeEvent.code.toString(),
          },
          extra: {
            url: serverwebSocket.url,
            code: closeEvent.code,
            reason: closeEvent.reason,
            wasClean: closeEvent.wasClean,
          },
        }
      );
    }

    dispatchWebSocketCloseEvent(serverwebSocket);
  };
}

async function initializeWebSocket(
  basePath: string,
  allowFailure = false
): Promise<{
  webSocket: WebSocket | undefined;
  serverAndClient: JSONRPCServerAndClient | undefined;
}> {
  const webSocket = await connectToWebSocket(basePath, allowFailure);

  if (!webSocket) {
    return { webSocket: undefined, serverAndClient: undefined };
  }

  const serverAndClient = new JSONRPCServerAndClient(
    new JSONRPCServer(),
    new JSONRPCClient((jsonRPCRequest) => {
      try {
        webSocket.send(JSON.stringify(jsonRPCRequest));
        return Promise.resolve();
      } catch (error) {
        console.error('Failed basePath:', basePath);
        console.error('Failed to send message:', error);
        if (!allowFailure) {
          const wsUrl = webSocket.url || ``;

          let errorMessage = 'Unknown error';
          if (error instanceof Error) {
            errorMessage = error.message;
          } else if (typeof error === 'string') {
            errorMessage = error;
          }

          dispatchCustomEvent('sessionConnectionError', {
            websocketUrl: wsUrl,
            error: errorMessage,
          });
        }
        return Promise.reject(error);
      }
    })
  );

  return { webSocket, serverAndClient };
}

export async function initializeOlWebSocket(
  hostname: string,
  protocol: string
): Promise<{
  webSocket: WebSocket | undefined;
  serverAndClient: JSONRPCServerAndClient | undefined;
}> {
  let olServerBasePath: string;
  const websocketProtocol = protocol.includes('https') ? 'wss' : 'ws';
  if (hostname.includes('localhost')) {
    const backendHostname = import.meta.env.VITE_LOCALHOST_BACKEND_HOSTNAME;
    olServerBasePath = `${websocketProtocol}://${backendHostname}/ws/olserver`;
  } else {
    olServerBasePath = `${websocketProtocol}://${hostname}/ws/olserver`;
  }

  const { webSocket, serverAndClient } = await initializeWebSocket(
    olServerBasePath
  );

  if (!webSocket || !serverAndClient) {
    return { webSocket: undefined, serverAndClient: undefined };
  }

  initializeWebSocketEventHandlers(webSocket, serverAndClient);

  return { webSocket, serverAndClient };
}

export async function initializeDbWebSocket(
  hostname: string,
  protocol: string
): Promise<{
  webSocket: WebSocket | undefined;
  serverAndClient: JSONRPCServerAndClient | undefined;
}> {
  let dbServerBasePath: string;
  const websocketProtocol = protocol.includes('https') ? 'wss' : 'ws';
  if (hostname.includes('localhost')) {
    const backendHostname = import.meta.env.VITE_LOCALHOST_BACKEND_HOSTNAME;
    dbServerBasePath = `${websocketProtocol}://${backendHostname}/ws/dbserver`;
  } else {
    dbServerBasePath = `${websocketProtocol}://${hostname}/ws/dbserver`;
  }

  const { webSocket, serverAndClient } = await initializeWebSocket(
    dbServerBasePath,
    true
  );

  if (!webSocket || !serverAndClient) {
    return { webSocket: undefined, serverAndClient: undefined };
  }

  initializeWebSocketEventHandlers(webSocket, serverAndClient);

  return { webSocket, serverAndClient };
}

export async function initializeReplayWebSocket(
  hostname: string,
  protocol: string
): Promise<{
  webSocket: WebSocket | undefined;
  serverAndClient: JSONRPCServerAndClient | undefined;
}> {
  let replayServerBasePath: string;
  const websocketProtocol = protocol.includes('https') ? 'wss' : 'ws';
  if (hostname.includes('localhost')) {
    const backendHostname = import.meta.env.VITE_LOCALHOST_BACKEND_HOSTNAME;
    replayServerBasePath = `${websocketProtocol}://${backendHostname}/ws/replay`;
  } else {
    replayServerBasePath = `${websocketProtocol}://${hostname}/ws/replay`;
  }

  const { webSocket, serverAndClient } = await initializeWebSocket(
    replayServerBasePath,
    true
  );

  if (!webSocket || !serverAndClient) {
    return { webSocket: undefined, serverAndClient: undefined };
  }

  initializeWebSocketEventHandlers(webSocket, serverAndClient);

  return { webSocket, serverAndClient };
}

export interface Sessions {
  olSession: Session | undefined;
  dbSession: Session | undefined;
  replaySession: Session | undefined;
  sessionId: string | undefined;
}

export interface WebSockets {
  olServerAndClient: JSONRPCServerAndClient | undefined;
  olWebsocket: WebSocket | undefined;
  dbServerAndClient: JSONRPCServerAndClient | undefined;
  dbWebsocket: WebSocket | undefined;
  replayServerAndClient: JSONRPCServerAndClient | undefined;
  replayWebsocket: WebSocket | undefined;
}

export interface ValidatedSessions {
  olSession: Session;
  dbSession: Session | undefined;
  replaySession: Session | undefined;
  sessionId: string;
}

export interface User {
  email: string;
  groups: string[];
  username: string;
  userId: string;
}

export const getEmptySessions = (): Sessions => ({
  olSession: undefined,
  dbSession: undefined,
  replaySession: undefined,
  sessionId: undefined,
});

export const getEmptyWebsockets = (): WebSockets => ({
  olServerAndClient: undefined,
  olWebsocket: undefined,
  dbServerAndClient: undefined,
  dbWebsocket: undefined,
  replayServerAndClient: undefined,
  replayWebsocket: undefined,
});

function extractLoginData(loginResponse: LoginResponse) {
  const sessionId = loginResponse['sessionUuid'];
  const email = loginResponse['email'];
  const userId = loginResponse['userUuid'];
  const groups =
    typeof loginResponse['groups'] === 'string'
      ? loginResponse['groups'].split(',').map((item) => item.trim())
      : [];
  const username = loginResponse['userName'];
  const reason = loginResponse['reason'];
  const success = loginResponse['success'];

  return { sessionId, email, groups, username, userId, reason, success };
}

export function isWebSocketError(response: any): boolean {
  return (
    typeof response === 'object' &&
    response !== null &&
    'success' in response &&
    !response.success
  );
}

// Helper to extract error message from response
function getErrorMessage(response: any): string {
  return (
    response.errorMessage || response.errorMsg || 'Unknown WebSocket error'
  );
}

// Helper to determine error type from response
function getErrorType(response: any): string {
  // Check for error in the response.error structure (JSON-RPC errors)
  if (response.error) {
    const errorMessage = response.error.message || '';
    if (errorMessage.includes('Invalid session ID')) {
      return 'invalid_session_id';
    }
    if (errorMessage.includes('SessionNotFound')) {
      return 'session_not_found';
    }
    if (
      errorMessage.includes('Invalid') &&
      errorMessage.includes('Parameters')
    ) {
      return 'invalid_parameters';
    }
    return 'websocket_message_error';
  }

  // Check for error messages in other fields (for backward compatibility)
  const errorMessage =
    response.errorMessage || response.errorMsg || response.message || '';
  if (errorMessage.includes('Invalid session ID')) {
    return 'invalid_session_id';
  }
  if (errorMessage.includes('SessionNotFound')) {
    return 'session_not_found';
  }

  return 'websocket_response_error';
}

// Helper to capture WebSocket errors with full context
function captureWebSocketError(
  error: Error | any,
  response: any,
  context: {
    method: string;
    params: any;
    sessionId: string;
    socketId: string;
    type: SessionType;
  }
): void {
  const errorType = getErrorType(response);
  const errorMessage =
    error instanceof Error ? error.message : getErrorMessage(response);

  // Note: Session error is already dispatched in onmessage handler for immediate detection
  // This is kept for logging purposes only

  Sentry.captureException(
    error instanceof Error ? error : new Error(errorMessage),
    {
      tags: {
        sessionType: context.type,
        method: context.method,
        socketId: context.socketId,
        hasSessionId: context.sessionId !== '',
        error_type: errorType,
      },
      extra: {
        response,
        params: context.params,
        errorMessage,
        sessionId: context.sessionId || 'no-session-id',
        // Include any additional error details from the response
        ...(response.error && { error: response.error }),
        ...(response.errorCode && { errorCode: response.errorCode }),
        ...(response.errorDetails && { errorDetails: response.errorDetails }),
        ...(response.stackTrace && { stackTrace: response.stackTrace }),
      },
      fingerprint: [
        'websocket-error',
        context.type,
        context.method,
        errorType,
        response.errorCode || 'no-error-code',
      ],
    }
  );
}

// Helper to build request parameters
function buildRequestParams(params: any, sessionId: string): any {
  if (sessionId === '') {
    return params;
  }

  return {
    ...params,
    sessionUuid: sessionId,
  };
}

export function createSession(
  serverAndClient: JSONRPCServerAndClient | undefined,
  webSocket: WebSocket | undefined,
  sessionId: string,
  type: SessionType
): Session | undefined {
  if (!serverAndClient) {
    return undefined;
  }

  const socketId = uuidv4();

  return {
    async request(method, params) {
      const finalParams = buildRequestParams(params, sessionId);
      const context = {
        method,
        params: finalParams,
        sessionId,
        socketId,
        type,
      };

      try {
        const response = await serverAndClient.request(method, finalParams);

        // Check if the response indicates an error
        if (isWebSocketError(response)) {
          const error = new Error(getErrorMessage(response));
          captureWebSocketError(error, response, context);
          throw error;
        }

        // Also check for JSON-RPC errors
        if (response && response.error) {
          const error = new Error(response.error.message || 'WebSocket error');
          captureWebSocketError(error, response, context);
          throw error;
        }

        return response;
      } catch (error) {
        // If error is already an Error object and not from our error handling above
        if (
          error instanceof Error &&
          !error.message.includes('WebSocket error') &&
          !error.message.includes('Unknown WebSocket error')
        ) {
          // This is likely a network error or timeout
          Sentry.captureException(error, {
            tags: {
              sessionType: type,
              method: method,
              socketId: socketId,
              hasSessionId: sessionId !== '',
              error_type: 'websocket_network_error',
            },
            extra: {
              params: finalParams,
              sessionId: sessionId || 'no-session-id',
              originalError: error.message,
              stack: error.stack,
            },
            fingerprint: [
              'websocket-network-error',
              type,
              method,
              error.name || 'Error',
            ],
          });
        }

        throw error;
      }
    },
    type,
    socketId,
    serverAndClient,
    webSocket,
  };
}

export async function initializeWebsockets() {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const { webSocket: olWebsocket, serverAndClient: olServerAndClient } =
    await initializeOlWebSocket(hostname, protocol);
  const { webSocket: dbWebsocket, serverAndClient: dbServerAndClient } =
    await initializeDbWebSocket(hostname, protocol);
  const { webSocket: replayWebsocket, serverAndClient: replayServerAndClient } =
    await initializeReplayWebSocket(hostname, protocol);

  return {
    olServerAndClient,
    olWebsocket,
    dbServerAndClient,
    dbWebsocket,
    replayServerAndClient,
    replayWebsocket,
  };
}

export type LoginErrorReason =
  | 'CONNECTION_ERROR'
  | 'INVALID_CREDENTIALS'
  | 'MISSING_SESSION'
  | 'MISSING_TOTP_CODE'
  | 'INVALID_TOTP_CODE'
  | 'MFA_REQUIRED';

function getEmptyResponse(webSockets: WebSockets, reason: LoginErrorReason) {
  const olSession = createSession(
    webSockets.olServerAndClient,
    webSockets.olWebsocket,
    '',
    'olSession'
  )!;
  const dbSession = createSession(
    webSockets.olServerAndClient,
    webSockets.dbWebsocket,
    '',
    'dbSession'
  )!;
  const replaySession = createSession(
    webSockets.olServerAndClient,
    webSockets.replayWebsocket,
    '',
    'replaySession'
  )!;

  return {
    sessions: {
      olSession: olSession,
      dbSession: dbSession,
      replaySession: replaySession,
      sessionId: undefined,
    },
    user: {
      email: '',
      groups: [],
      username: '',
      userId: '',
    },
    reason: reason,
  };
}

export async function createClientWithCredentials(
  credentials: {
    username: string;
    password: string;
  },
  webSockets: WebSockets,
  totpCode?: number,
  timeZone?: string
): Promise<{ sessions: Sessions; user: User; reason?: LoginErrorReason }> {
  if (!webSockets.olServerAndClient) {
    return getEmptyResponse(webSockets, 'CONNECTION_ERROR');
  }

  const basePayload = {
    userName: credentials.username,
    password: credentials.password,
    serverUid: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
    locale: 'en_US',
    timeZone: timeZone ? timeZone : '',
    defaultTimeZone: 'UTC',
    stealSession: true,
    sessionUuid: '',
    applicationName: 'marinepulse',
  };

  const payload = totpCode ? { ...basePayload, totpCode } : basePayload;
  const loginResponse: LoginResponse =
    await webSockets.olServerAndClient.request('login', payload);
  const { sessionId, email, groups, username, userId, reason, success } =
    extractLoginData(loginResponse);

  if (!success && reason) {
    return getEmptyResponse(webSockets, reason as LoginErrorReason);
  } else if (!success) {
    return getEmptyResponse(webSockets, 'INVALID_CREDENTIALS');
  }

  const hostname = window.location.hostname;
  if (hostname.includes('localhost')) {
    localStorage.setItem('credentials', JSON.stringify(credentials));
  }

  // All the sessions here use the same session id, which is maintained by the backend
  const olSession = createSession(
    webSockets.olServerAndClient,
    webSockets.olWebsocket,
    sessionId,
    'olSession'
  )!;
  const dbSession = createSession(
    webSockets.dbServerAndClient,
    webSockets.dbWebsocket,
    sessionId,
    'dbSession'
  );
  const replaySession = createSession(
    webSockets.replayServerAndClient,
    webSockets.replayWebsocket,
    sessionId,
    'replaySession'
  );

  return {
    sessions: {
      olSession,
      dbSession,
      replaySession,
      sessionId,
    },
    user: {
      email,
      groups,
      username,
      userId,
    },
  };
}

export async function createClientWithAzure(
  idToken: string,
  accessToken: string,
  webSockets: WebSockets,
  timeZone?: string
): Promise<{ sessions: Sessions; user: User }> {
  if (!webSockets.olServerAndClient) {
    return getEmptyResponse(webSockets, 'INVALID_CREDENTIALS');
  }

  const loginResponse: LoginResponse =
    await webSockets.olServerAndClient.request('loginAzureAD', {
      idToken: idToken,
      accessToken: accessToken,
      serverUid: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
      locale: 'en_US',
      timeZone: timeZone ? timeZone : '',
      defaultTimeZone: 'UTC',
      stealSession: true,
      sessionUuid: '',
      applicationName: 'marinepulse',
    });
  const { sessionId, email, groups, username, userId, reason, success } =
    extractLoginData(loginResponse);

  // All the sessions here use the same session id, which is maintained by the backend
  if (!success) {
    return getEmptyResponse(webSockets, reason as LoginErrorReason);
  }

  const olSession = createSession(
    webSockets.olServerAndClient,
    webSockets.olWebsocket,
    sessionId,
    'olSession'
  )!;
  const dbSession = createSession(
    webSockets.dbServerAndClient,
    webSockets.dbWebsocket,
    sessionId,
    'dbSession'
  );
  const replaySession = createSession(
    webSockets.replayServerAndClient,
    webSockets.replayWebsocket,
    sessionId,
    'replaySession'
  );

  return {
    sessions: {
      olSession,
      dbSession,
      replaySession,
      sessionId,
    },
    user: {
      email,
      groups,
      username,
      userId,
    },
  };
}

function connectToWebSocket(
  url: string,
  allowFailure: boolean
): Promise<WebSocket | undefined> {
  return new Promise((resolve, reject) => {
    const socket = new WebSocket(url);

    socket.onopen = () => {
      if (socket.readyState === WebSocket.OPEN) {
        resolve(socket);
      }
    };

    socket.onerror = (event) => {
      console.error('WebSocket error observed:', event);
      if (!allowFailure) {
        dispatchCustomEvent('sessionConnectionError', {
          websocketUrl: socket.url,
          error: 'WebSocket connection error',
        });
        reject(new Error('Failed to open WebSocket connection.'));
      }
    };

    socket.onclose = (event) => {
      if (!event.wasClean && !allowFailure) {
        console.error('WebSocket closed unexpectedly:', event);

        dispatchCustomEvent('sessionConnectionError', {
          websocketUrl: socket.url,
          error: 'WebSocket connection closed unexpectedly',
          code: event?.code,
          reason: event?.reason,
        });

        dispatchWebSocketCloseEvent(socket);
        reject(new Error('WebSocket was closed uncleanly.'));
      } else {
        console.warn('WebSocket closed', event);
        resolve(undefined);
      }
    };
  });
}
